// src/components/admin/attendance/RegulateAttendance.js
import React, { useState, useEffect } from 'react';
import { UserPlus, Calendar, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { useEmployees } from '../../../contexts/EmployeeContext';
import { api } from '../../../services/api';
import { getCurrentISTDate } from '../../../utils/dateHelpers';

// Helper to get today's date in IST as YYYY-MM-DD
const getTodayDate = () => {
    return getCurrentISTDate();
};

const RegulateAttendance = () => {
    const { employees } = useEmployees();
    const [activeTab, setActiveTab] = useState('attendance'); // 'attendance' or 'leave'
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    // Attendance form state
    const [attendanceForm, setAttendanceForm] = useState({
        employeeId: '',
        fromDate: getTodayDate(),
        toDate: getTodayDate(),
        checkInTime: '09:00',
        checkOutTime: '18:00',
        status: 'present',
        location: 'Office',
        notes: ''
    });

    // Leave form state
    const [leaveForm, setLeaveForm] = useState({
        employeeId: '',
        leaveTypeId: '',
        fromDate: getTodayDate(),
        toDate: getTodayDate(),
        reason: '',
        isHalfDay: false,
        halfDayType: 'first_half'
    });

    const [leaveTypes, setLeaveTypes] = useState([]);
    const [employeeLeaveBalances, setEmployeeLeaveBalances] = useState([]);
    const [loadingLeaveTypes, setLoadingLeaveTypes] = useState(false);
    const [noLeavePlanMessage, setNoLeavePlanMessage] = useState('');

    // Fetch leave types and balances when employee is selected
    useEffect(() => {
        if (leaveForm.employeeId) {
            fetchEmployeeLeaveTypes(leaveForm.employeeId);
        } else {
            setLeaveTypes([]);
            setEmployeeLeaveBalances([]);
            setNoLeavePlanMessage('');
        }
    }, [leaveForm.employeeId]);

    const fetchEmployeeLeaveTypes = async (employeeId) => {
        try {
            setLoadingLeaveTypes(true);
            setNoLeavePlanMessage('');

            const response = await api.get(`/api/admin/employees/${employeeId}/leave-types`);

            if (response.data.leaveTypes && response.data.leaveTypes.length > 0) {
                setEmployeeLeaveBalances(response.data.leaveTypes);
                setLeaveTypes(response.data.leaveTypes);
            } else {
                setEmployeeLeaveBalances([]);
                setLeaveTypes([]);
                setNoLeavePlanMessage(response.data.message || 'No leave types available for this employee');
            }
        } catch (error) {
            console.error('Error fetching employee leave types:', error);
            setLeaveTypes([]);
            setEmployeeLeaveBalances([]);
            setNoLeavePlanMessage('Failed to fetch leave types. Please try again.');
        } finally {
            setLoadingLeaveTypes(false);
        }
    };

    const handleAttendanceSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSuccessMessage('');
        setErrorMessage('');

        try {
            // Validate date range
            const fromDate = new Date(attendanceForm.fromDate);
            const toDate = new Date(attendanceForm.toDate);

            if (toDate < fromDate) {
                setErrorMessage('To Date must be after From Date');
                setLoading(false);
                return;
            }

            // Calculate number of days
            const daysDiff = Math.ceil((toDate - fromDate) / (1000 * 60 * 60 * 24)) + 1;

            if (daysDiff > 31) {
                setErrorMessage('Date range cannot exceed 31 days');
                setLoading(false);
                return;
            }

            // Use admin endpoint to add attendance for date range
            const response = await api.post('/api/admin/attendance/bulk-add', {
                employeeId: attendanceForm.employeeId,
                fromDate: attendanceForm.fromDate,
                toDate: attendanceForm.toDate,
                checkInTime: attendanceForm.checkInTime,
                checkOutTime: attendanceForm.checkOutTime,
                status: attendanceForm.status,
                location: attendanceForm.location,
                notes: attendanceForm.notes,
                isAdminEntry: true // Flag to bypass approval
            });

            const recordsAdded = response.data.recordsAdded || daysDiff;
            setSuccessMessage(`Attendance added successfully for ${recordsAdded} day(s)!`);

            // Reset form
            setAttendanceForm({
                employeeId: '',
                fromDate: getTodayDate(),
                toDate: getTodayDate(),
                checkInTime: '09:00',
                checkOutTime: '18:00',
                status: 'present',
                location: 'Office',
                notes: ''
            });
        } catch (error) {
            console.error('Error adding attendance:', error);
            setErrorMessage(error.response?.data?.message || 'Failed to add attendance');
        } finally {
            setLoading(false);
        }
    };

    const handleLeaveSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSuccessMessage('');
        setErrorMessage('');

        try {
            // Use admin endpoint to directly add leave without approval
            const response = await api.post('/api/admin/leaves/add', {
                employeeId: leaveForm.employeeId,
                leaveTypeId: leaveForm.leaveTypeId,
                fromDate: leaveForm.fromDate,
                toDate: leaveForm.toDate,
                reason: leaveForm.reason,
                isHalfDay: leaveForm.isHalfDay,
                halfDayType: leaveForm.halfDayType,
                status: 'approved', // Auto-approve admin entries
                isAdminEntry: true
            });

            setSuccessMessage('Leave added successfully!');
            // Reset form
            setLeaveForm({
                employeeId: '',
                leaveTypeId: '',
                fromDate: getTodayDate(),
                toDate: getTodayDate(),
                reason: '',
                isHalfDay: false,
                halfDayType: 'first_half'
            });
        } catch (error) {
            console.error('Error adding leave:', error);
            setErrorMessage(error.response?.data?.message || 'Failed to add leave');
        } finally {
            setLoading(false);
        }
    };

    const selectedEmployee = employees.find(emp => emp.id === attendanceForm.employeeId || emp.id === leaveForm.employeeId);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <UserPlus className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Regulate Attendance</h2>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 dark:border-blue-500 p-4 rounded-lg">
                <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-300 mr-3 mt-0.5" />
                    <div>
                        <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                            Admin Direct Entry
                        </p>
                        <p className="text-xs text-blue-700 dark:text-blue-200 mt-1">
                            Attendance and leave entries added here are automatically approved and do not require manager approval.
                        </p>
                    </div>
                </div>
            </div>

            {/* Success/Error Messages */}
            {successMessage && (
                <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-400 dark:border-green-500 p-4 rounded-lg">
                    <div className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-300 mr-3" />
                        <p className="text-sm text-green-700 dark:text-green-200">{successMessage}</p>
                    </div>
                </div>
            )}

            {errorMessage && (
                <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 dark:border-red-500 p-4 rounded-lg">
                    <div className="flex items-center">
                        <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-300 mr-3" />
                        <p className="text-sm text-red-700 dark:text-red-200">{errorMessage}</p>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                <div className="border-b dark:border-gray-700">
                    <nav className="flex">
                        <button
                            onClick={() => setActiveTab('attendance')}
                            className={`px-6 py-3 text-sm font-medium border-b-2 ${
                                activeTab === 'attendance'
                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                        >
                            <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4" />
                                Add Attendance
                            </div>
                        </button>
                        <button
                            onClick={() => setActiveTab('leave')}
                            className={`px-6 py-3 text-sm font-medium border-b-2 ${
                                activeTab === 'leave'
                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                        >
                            <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                Add Leave
                            </div>
                        </button>
                    </nav>
                </div>

                {/* Attendance Form */}
                {activeTab === 'attendance' && (
                    <form onSubmit={handleAttendanceSubmit} className="p-6 space-y-6">
                        <div className="grid grid-cols-2 gap-6">
                            {/* Employee Selection */}
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Select Employee <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={attendanceForm.employeeId}
                                    onChange={(e) => setAttendanceForm({ ...attendanceForm, employeeId: e.target.value })}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                                >
                                    <option value="">Choose an employee...</option>
                                    {employees.map(emp => (
                                        <option key={emp.id} value={emp.id}>
                                            {emp.first_name} {emp.last_name} ({emp.employee_code}) - {emp.department}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* From Date */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    From Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    value={attendanceForm.fromDate}
                                    onChange={(e) => setAttendanceForm({ ...attendanceForm, fromDate: e.target.value })}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                                />
                            </div>

                            {/* To Date */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    To Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    value={attendanceForm.toDate}
                                    onChange={(e) => setAttendanceForm({ ...attendanceForm, toDate: e.target.value })}
                                    required
                                    min={attendanceForm.fromDate}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                                />
                            </div>

                            {/* Status */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Status <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={attendanceForm.status}
                                    onChange={(e) => setAttendanceForm({ ...attendanceForm, status: e.target.value })}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                                >
                                    <option value="present">Present</option>
                                    <option value="absent">Absent</option>
                                    <option value="half-day">Half Day</option>
                                    <option value="holiday">Holiday</option>
                                </select>
                            </div>

                            {/* Check In Time */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Check In Time
                                </label>
                                <input
                                    type="time"
                                    value={attendanceForm.checkInTime}
                                    onChange={(e) => setAttendanceForm({ ...attendanceForm, checkInTime: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                                />
                            </div>

                            {/* Check Out Time */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Check Out Time
                                </label>
                                <input
                                    type="time"
                                    value={attendanceForm.checkOutTime}
                                    onChange={(e) => setAttendanceForm({ ...attendanceForm, checkOutTime: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                                />
                            </div>

                            {/* Location */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Location
                                </label>
                                <select
                                    value={attendanceForm.location}
                                    onChange={(e) => setAttendanceForm({ ...attendanceForm, location: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                                >
                                    <option value="Office">Office</option>
                                    <option value="WFH">Work From Home</option>
                                    <option value="Remote">Remote</option>
                                    <option value="Client Site">Client Site</option>
                                </select>
                            </div>

                            {/* Notes */}
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Notes
                                </label>
                                <textarea
                                    value={attendanceForm.notes}
                                    onChange={(e) => setAttendanceForm({ ...attendanceForm, notes: e.target.value })}
                                    rows="3"
                                    placeholder="Add any notes or remarks..."
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setAttendanceForm({
                                    employeeId: '',
                                    fromDate: getTodayDate(),
                                    toDate: getTodayDate(),
                                    checkInTime: '09:00',
                                    checkOutTime: '18:00',
                                    status: 'present',
                                    location: 'Office',
                                    notes: ''
                                })}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                                Reset
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        Adding...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="w-4 h-4" />
                                        Add Attendance
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                )}

                {/* Leave Form */}
                {activeTab === 'leave' && (
                    <form onSubmit={handleLeaveSubmit} className="p-6 space-y-6">
                        <div className="grid grid-cols-2 gap-6">
                            {/* Employee Selection */}
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Select Employee <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={leaveForm.employeeId}
                                    onChange={(e) => setLeaveForm({ ...leaveForm, employeeId: e.target.value })}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                                >
                                    <option value="">Choose an employee...</option>
                                    {employees.map(emp => (
                                        <option key={emp.id} value={emp.id}>
                                            {emp.first_name} {emp.last_name} ({emp.employee_code}) - {emp.department}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Leave Type */}
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Leave Type <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={leaveForm.leaveTypeId}
                                    onChange={(e) => setLeaveForm({ ...leaveForm, leaveTypeId: e.target.value })}
                                    required
                                    disabled={!leaveForm.employeeId || loadingLeaveTypes || leaveTypes.length === 0}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                                >
                                    <option value="">
                                        {!leaveForm.employeeId
                                            ? 'Select an employee first...'
                                            : loadingLeaveTypes
                                                ? 'Loading leave types...'
                                                : leaveTypes.length === 0
                                                    ? 'No leave types available'
                                                    : 'Select leave type...'}
                                    </option>
                                    {leaveTypes.map(type => (
                                        <option key={type.leave_type_id} value={type.leave_type_id}>
                                            {type.name} ({type.code}) - Available: {type.remaining_days} days
                                        </option>
                                    ))}
                                </select>

                                {/* Show no leave plan message if applicable */}
                                {noLeavePlanMessage && leaveForm.employeeId && (
                                    <p className="mt-2 text-sm text-yellow-600 dark:text-yellow-400">
                                        {noLeavePlanMessage}
                                    </p>
                                )}
                            </div>

                            {/* Display balance information for selected leave type */}
                            {leaveForm.leaveTypeId && employeeLeaveBalances.length > 0 && (
                                <div className="col-span-2">
                                    {(() => {
                                        const selectedLeaveType = employeeLeaveBalances.find(
                                            lt => lt.leave_type_id === leaveForm.leaveTypeId
                                        );
                                        if (!selectedLeaveType) return null;

                                        return (
                                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                                                <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
                                                    Leave Balance for {selectedLeaveType.name} ({selectedLeaveType.year})
                                                </h4>
                                                <div className="grid grid-cols-3 gap-4 text-sm">
                                                    <div>
                                                        <span className="text-gray-600 dark:text-gray-400">Total Days:</span>
                                                        <p className="font-semibold text-gray-900 dark:text-white">
                                                            {selectedLeaveType.total_days}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-600 dark:text-gray-400">Used Days:</span>
                                                        <p className="font-semibold text-orange-600 dark:text-orange-400">
                                                            {selectedLeaveType.used_days}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-600 dark:text-gray-400">Remaining Days:</span>
                                                        <p className="font-semibold text-green-600 dark:text-green-400">
                                                            {selectedLeaveType.remaining_days}
                                                        </p>
                                                    </div>
                                                </div>
                                                {selectedLeaveType.carry_forward && (
                                                    <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                                                        Carry forward enabled (Max: {selectedLeaveType.max_carry_forward_days || 'Unlimited'} days)
                                                    </p>
                                                )}
                                                {selectedLeaveType.is_encashable && (
                                                    <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                                                        This leave type is encashable
                                                    </p>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}

                            {/* From Date */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    From Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    value={leaveForm.fromDate}
                                    onChange={(e) => setLeaveForm({ ...leaveForm, fromDate: e.target.value })}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                                />
                            </div>

                            {/* To Date */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    To Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    value={leaveForm.toDate}
                                    onChange={(e) => setLeaveForm({ ...leaveForm, toDate: e.target.value })}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                                />
                            </div>

                            {/* Half Day Toggle */}
                            <div className="col-span-2">
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={leaveForm.isHalfDay}
                                        onChange={(e) => setLeaveForm({ ...leaveForm, isHalfDay: e.target.checked })}
                                        className="rounded border-gray-300 dark:border-gray-600"
                                    />
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Half Day Leave</span>
                                </label>
                            </div>

                            {/* Half Day Type */}
                            {leaveForm.isHalfDay && (
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Half Day Type
                                    </label>
                                    <select
                                        value={leaveForm.halfDayType}
                                        onChange={(e) => setLeaveForm({ ...leaveForm, halfDayType: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                                    >
                                        <option value="first_half">First Half</option>
                                        <option value="second_half">Second Half</option>
                                    </select>
                                </div>
                            )}

                            {/* Reason */}
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Reason <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={leaveForm.reason}
                                    onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })}
                                    required
                                    rows="3"
                                    placeholder="Enter reason for leave..."
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setLeaveForm({
                                    employeeId: '',
                                    leaveTypeId: '',
                                    fromDate: getTodayDate(),
                                    toDate: getTodayDate(),
                                    reason: '',
                                    isHalfDay: false,
                                    halfDayType: 'first_half'
                                })}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                                Reset
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        Adding...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="w-4 h-4" />
                                        Add Leave
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default RegulateAttendance;
